import MCButton from '@/components/ui/Buttons/MobicomButton'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Loader from '@/components/ui/loader'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useMobiApi } from '@/services/mobi-api'
import { TanConfirmReq, TanConfirmResult, TanCreateReq, TanCreateResponse } from '@/services/mobi-api/sim/types'
import { resolveImageUrl } from '@/utils/constants'
import useIntl from '@/utils/intl'
import { Close, Loop } from '@mui/icons-material'
import { Dialog, Divider, Slide } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import Image from 'next/image'
import { FunctionComponent, forwardRef, useEffect, useReducer, useState } from 'react'
import OTPInput from 'react-otp-input'

interface Props {
  onClick: (req?: TanCreateReq) => void
  payload: TanCreateReq | undefined
}
interface State {
  loading?: boolean
  buttonLoading?: boolean
  otp?: string
  tanRes?: TanCreateResponse
  confirmRes?: TanConfirmResult
  email?: string
  tanLocked?: boolean
  tanIdentifierLocked?: boolean
  dialogOpen?: boolean
  errorMessageMn?: string
  errorMessageEn?: string
  timerId?: NodeJS.Timer
}
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />
})
const Step3: FunctionComponent<Props> = ({ onClick, payload }) => {
  const { showError } = useToast()
  const { translate } = useIntl()
  const { locale } = useLanguage()
  const { sim } = useMobiApi()
  const [counter, setCounter] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      loading: true,
      buttonLoading: false,
      otp: '',
      email: '',
      tanLocked: false,
      tanIdentifierLocked: false,
      dialogOpen: false
    }
  )

  useEffect(() => {
    void handleTanCreate()
    return () => clearTimer()
  }, [])

  const handleTanCreate = async (id?: string): Promise<void> => {
    let req: TanCreateReq = {}
    if (payload !== undefined) {
      req = payload
    }
    if (id !== undefined) {
      req = { id }
    }
    try {
      clearTimer()
      setState({
        loading: true,
        errorMessageEn: undefined,
        errorMessageMn: undefined
      })
      const res = await sim.tanCreate(req)
      if (res?.error !== undefined) {
        const { messageMn = translate('request.failed'), messageEn = translate('request.failed') } = res.cause ?? {}
        setState({
          errorMessageMn: messageMn ?? 'error',
          errorMessageEn: messageEn ?? (messageMn ?? 'error')
        })
        showError(locale === 'en' ? messageEn : messageMn)
      }
      if (res?.response !== null && res?.response !== undefined && res.responseCode === 'SUCCESS') {
        const emailArr = res.responseDescription.split(' ')
        const emailStr = emailArr[emailArr.length - 1]
        setState({ tanRes: res?.response, email: emailStr })
        setCounter(res.response.timeLeftInSec)
        startTimer()
      }
      if (res?.responseCode === 'TAN_CREATION_LOCKED') {
        setState({ tanRes: res?.response, tanLocked: true })
      }
      if (res?.responseCode === 'TAN_IDENTIFIER_LOCKED') {
        setState({ tanRes: res?.response, tanIdentifierLocked: true })
      }
    } catch (e) {
      showError(translate('request.failed'))
    } finally {
      setState({ loading: false, otp: '' })
    }
  }
  const handleTanConfirm = async (): Promise<void> => {
    if (isSubmitting) return

    if (state.tanRes?.simRecoveryId === undefined) {
      return
    }
    try {
      setIsSubmitting(true)
      const tanConfirmPayload: TanConfirmReq = {
        id: state.tanRes.simRecoveryId,
        code: state.otp ?? ''
      }
      const res = await sim.tanConfirm(tanConfirmPayload)
      if (res !== undefined) {
        if (res.redirectUrl !== undefined && res.redirectUrl !== null && res.redirectUrl !== '') {
          setState({ confirmRes: res, dialogOpen: true })
        } else if (res.responseCode === 'SUCCESS') {
          onClick({
            ...payload,
            id: state.tanRes.simRecoveryId
          })
        }
        if (res.responseCode === 'TAN_MISMATCHED') {
          showError(translate('sim.tan_mismatch'))
        }
        if (res.responseCode === 'TAN_NOT_FOUND') {
          showError(translate('sim.tan_mismatch'))
        }
        if (res.responseCode === 'TAN_LOCKED') {
          setState({ otp: '' })
          clearTimer()
          showError(translate('sim.tan_locked'))
        }
        if (res?.error !== undefined) {
          showError(translate('request.failed'))
        }
      } else {
        showError(translate('request.failed'))
      }
    } catch (e) {
      showError(translate('request.failed'))
    } finally {
      setIsSubmitting(false)
      setState({ loading: false })
    }
  }
  const startTimer = (): void => {
    const intervalId = setInterval(() => {
      setCounter((prev) => prev - 1)
    }, 1000)
    setState({ timerId: intervalId })
  }
  const clearTimer = (): void => {
    clearInterval(state.timerId)
    setState({ timerId: undefined })
    setCounter(0)
  }
  const renderTimer = (): string => {
    if (counter !== undefined && counter > 0) {
      const min = Math.floor(counter / 60)
      const sec = Math.floor(counter % 60)
      const minStr = min < 10 ? '0' + min.toString() : min.toString()
      const secStr = sec < 10 ? '0' + sec.toString() : sec.toString()

      return minStr + ':' + secStr
    } else {
      return '00:00'
    }
  }
  const renderDuration = (time: number | undefined): string => {
    if (time === undefined || time === null) {
      return '?'
    }
    const hours = Math.floor(time / 3600)
    const minutes = Math.floor((time - (hours * 3600)) / 60)
    const minutesStr: string = minutes < 10 ? '0' + minutes.toString() : minutes.toString()

    if (locale === 'en') {
      return hours > 0 ? hours.toString() + ' hours and ' + minutesStr + ' minutes' : minutesStr + ' minutes'
    } else {
      return hours > 0 ? hours.toString() + ' цаг ' + minutesStr + ' минутын ' : minutesStr + ' минутын '
    }
  }
  if (state.loading === true) {
    return <div className='w-full h-96 flex items-center justify-center'><Loader /></div>
  }
  if (state.tanLocked === true) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex items-center justify-center '>
        <p className='text-center text-textHigh w-2/3'>
          {translate('sim.tan_creation_locked').replace('$', renderDuration(state.tanRes?.creationLimit.lockTimeLeftSec))}
        </p>
      </div>
    )
  }
  if (state.tanIdentifierLocked === true) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow h-full md:h-[75vh] w-96 flex flex-col justify-center items-center overflow-auto relative p-4 md:p-6'>
        <p className='text-center text-textHigh'>{translate('sim.tan_creation_locked').replace('$', renderDuration(state.tanRes?.identifierLock?.timeLeftInSec))}</p>
      </div>
    )
  }
  if (state.tanRes === undefined) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow mt-10 h-full md:h-[75vh] w-96 flex flex-col justify-center items-center overflow-auto relative p-4 md:p-6'>
        <p className='text-center text-textHigh'>{locale === 'mn' ? state.errorMessageMn : state.errorMessageEn ?? translate('request.failed')}</p>
        <Image draggable={false} width={1000} height={1000} className='w-fit h-fit mt-4' src={resolveImageUrl('/uploads/icons/mobot.svg')} alt='mobot' />
      </div>
    )
  }
  return (
    <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-start overflow-auto relative'>
      <div className='py-6 px-4 md:p-8 flex flex-col gap-4 items-center md:justify-center '>
        <p className='text-textMid font-medium text-center w-96'>{translate('sim.tan_sent').replace('$email', state.email ?? '')}</p>
        <div className='w-full flex justify-center h-fit'>
          <OTPInput
            value={state.otp}
            onChange={(otp) => setState({ otp })}
            numInputs={6}
            renderSeparator={<Divider orientation='vertical' />}
            renderInput={(props: any) => <input {...props} />}
            inputType='number'
            inputStyle={{
              width: '45px',
              height: '45px',
              fontSize: '18px',
              color: 'textHigh',
              fontWeight: '700',
              caretColor: 'blue',
              borderRadius: '8px',
              backgroundColor: 'surfaceInverse'
            }}
            containerStyle={{
              border: '1px solid #CFD3DB',
              borderRadius: '8px',
              outline: 'none',
              backgroundColor: 'surfaceInverse'
            }}
          />
        </div>
        {
          state.tanRes !== undefined && (
            <span className='text-textMid'>{renderTimer()}</span>
          )
        }
        {
          state.tanRes !== undefined && (
            <MCButton
              onClick={() => { void handleTanCreate(state.tanRes?.simRecoveryId) }}
              type='outlined' leadingIcon={<Loop />}
              disabled={state.tanRes.creationLimit.limit - state.tanRes.creationLimit.count === 0}
            >
              {translate('sim.tan.getagain')}
              {state.tanRes.creationLimit.limit - state.tanRes.creationLimit.count}
            </MCButton>
          )
        }
      </div>
      <div className='h-[90vh]' />
      <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <DarkButton fullwidth disabled={state.otp?.length !== 6} onClick={() => { void handleTanConfirm() }}>{isSubmitting ? <Loader /> : translate('continue')}</DarkButton>
      </div>
      <Dialog
        open={state.dialogOpen === true}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => { setState({ dialogOpen: false }) }}
        aria-describedby='alert-dialog-slide-description'
      >
        <div className='bg-surfaceInverse'>
          <div className='flex justify-end mt-3 mr-3'>
            <div className='text-textHigh bg-white/10 p-2 rounded-xl cursor-pointer' onClick={() => setState({ dialogOpen: false })}>
              <Close />
            </div>
          </div>
          <div className='px-14 pb-14 flex flex-col gap-6 items-center'>
            <Image draggable={false} width={1000} height={1000} className='w-fit h-fit' src={resolveImageUrl('/uploads/icons/mobot.svg')} alt='mobot' />
            <div>
              <h1 className='text-textHigh font-bold text-xl text-center'>{translate('dan.title')}</h1>
              <p className='text-textMid text-sm font-medium text-center'>{translate('dan.announce')}</p>
            </div>
          </div>
          <Divider />
          <div className='px-8 pb-8 pt-4'>
            <MCButton fullwidth onClick={() => { onClick({ ...payload, id: state.tanRes?.simRecoveryId }) }}>{translate('accept')}</MCButton>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
export default Step3
